import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Calendar, Plus, Edit, Trash2, Eye, EyeOff, AlertCircle, CheckCircle, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import eventService from '../services/eventService';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import TimeSlotManager from '../components/TimeSlotManager';
import MultiDayCalendar from '../components/MultiDayCalendar';
import './EventsManagementPage.css';

// 🎯 Imagem placeholder enquanto carrega
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="%23999"%3ECarregando...%3C/text%3E%3C/svg%3E';

export default function EventsManagementPage() {
  const [currentPage, setCurrentPage] = useState('list');
  const [events, setEvents] = useState([]);
  const [eventImages, setEventImages] = useState({}); // 🆕 Estado para imagens
  const [loading, setLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const { alertProps, showSuccess, showError, showWarning } = useCustomAlert();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    price: '',
    maxReservations: '',
    eventType: 'single',
    timeSlots: [],
  });
  const [previewUrls, setPreviewUrls] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [dailyDetails, setDailyDetails] = useState({});

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await eventService.getEventsOptimizedAdmin();
      setEvents(data);
      setLoading(false);

      loadEventImages(data);
    } catch (error) {
      showError('Erro ao carregar eventos');
      setLoading(false);
    }
  };

  const loadEventImages = async (eventsToLoad) => {
    const imagePromises = eventsToLoad.map(event =>
      eventService.getEventImagesAdmin(event.id)
        .then(images => ({ id: event.id, images }))
        .catch(() => ({ id: event.id, images: { images: [], dailyDetails: [] } }))
    );

    const results = await Promise.all(imagePromises);
    const imagesMap = {};
    results.forEach(result => imagesMap[result.id] = result.images);
    setEventImages(imagesMap);
  };

  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      price: '',
      maxReservations: '',
      eventType: 'single',
      timeSlots: [],
    });
    setPreviewUrls([]);
    setSelectedDates([]);
    setDailyDetails({});
    setEditingEvent(null);
  }, []);

  const startEdit = useCallback(async (event) => {
    try {
      setLoading(true);
      console.log('📝 Editando evento:', event.id);

      const fullEventData = await eventService.getEvent(event.id);
      console.log('✅ Dados completos carregados:', fullEventData);

      setEditingEvent(fullEventData);

      const normalizedTimeSlots = (fullEventData.timeSlots || []).map(slot => ({
        id: slot.id || Date.now() + Math.random(),
        name: slot.name,
        startTime: slot.startTime,
        endTime: slot.endTime,
        price: parseFloat(slot.price),
        maxCapacity: parseInt(slot.maxCapacity),
        currentBookings: slot.currentBookings || 0
      }));

      setFormData({
        title: fullEventData.title,
        description: fullEventData.description,
        startDate: fullEventData.startDate.split('T')[0],
        endDate: fullEventData.endDate ? fullEventData.endDate.split('T')[0] : '',
        price: fullEventData.price.toString(),
        maxReservations: fullEventData.maxReservations.toString(),
        eventType: fullEventData.eventType,
        timeSlots: normalizedTimeSlots,
      });

      setPreviewUrls(fullEventData.images || []);

      const toLocalDateStr = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      if (fullEventData.eventType === 'multi') {
        let dates = [];

        if (fullEventData.dailyDetails && fullEventData.dailyDetails.length > 0) {
          dates = fullEventData.dailyDetails.map(d => {
            // Parse date safely (handles both "2025-04-01" and "2025-04-01T00:00:00")
            const datePart = d.date.split('T')[0];
            const [yr, mo, dy] = datePart.split('-').map(Number);
            const date = new Date(yr, mo - 1, dy, 12, 0, 0, 0);
            return date;
          });

          const details = {};
          fullEventData.dailyDetails.forEach(d => {
            // Normalize key to YYYY-MM-DD (strip time part if present)
            const key = d.date.split('T')[0];
            details[key] = {
              image: d.image,
              description: d.description
            };
          });
          setDailyDetails(details);
        } else if (fullEventData.startDate && fullEventData.endDate) {
          const start = new Date(fullEventData.startDate.split('T')[0]);
          const end = new Date(fullEventData.endDate.split('T')[0]);

          start.setHours(12, 0, 0, 0);
          end.setHours(12, 0, 0, 0);

          const currentDate = new Date(start);
          while (currentDate <= end) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
          }

          console.log(`📅 Calculadas ${dates.length} datas:`,
            dates.map(d => d.toLocaleDateString('pt-PT')));

          setDailyDetails({});
        }

        setSelectedDates(dates);
      } else {
        setSelectedDates([]);
        setDailyDetails({});
      }

      setCurrentPage('edit');
    } catch (error) {
      console.error('❌ Erro ao carregar dados do evento:', error);
      showError('Erro ao carregar dados do evento');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleDeleteEvent = useCallback(async (id) => {
    if (window.confirm('Tens a certeza que queres eliminar este evento?')) {
      try {
        await eventService.deleteEvent(id);
        showSuccess('Evento eliminado com sucesso!');
        loadEvents();
      } catch (error) {
        showError('Erro ao eliminar evento');
      }
    }
  }, [showSuccess, showError]);

  const handleToggleStatus = useCallback(async (id, currentStatus) => {
    const action = currentStatus ? 'desativar' : 'ativar';
    if (window.confirm(`Tens a certeza que queres ${action} este evento?`)) {
      try {
        await eventService.toggleEventStatus(id);
        showSuccess(`Evento ${currentStatus ? 'desativado' : 'ativado'} com sucesso!`);
        loadEvents();
      } catch (error) {
        showError('Erro ao alterar status');
      }
    }
  }, [showSuccess, showError]);

  const handleCreateClick = useCallback(() => {
    resetForm();
    setCurrentPage('create');
  }, [resetForm]);

  const handleCancelForm = useCallback(() => {
    resetForm();
    setCurrentPage('list');
  }, [resetForm]);

  const handleSuccessForm = useCallback(() => {
    resetForm();
    setCurrentPage('list');
    loadEvents();
  }, [resetForm]);

  return (
    <div className="events-management-system">
      <CustomAlert {...alertProps} />

      <Header currentPage={currentPage} setCurrentPage={setCurrentPage} resetForm={resetForm} />

      <main className="events-main">
        {currentPage === 'list' && (
          <EventsList
            events={events}
            eventImages={eventImages}
            loading={loading}
            onEdit={startEdit}
            onDelete={handleDeleteEvent}
            onToggleStatus={handleToggleStatus}
            onCreate={handleCreateClick}
          />
        )}

        {(currentPage === 'create' || currentPage === 'edit') && (
          <EventForm
            isEdit={currentPage === 'edit'}
            editingEvent={editingEvent}
            formData={formData}
            setFormData={setFormData}
            previewUrls={previewUrls}
            setPreviewUrls={setPreviewUrls}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            dailyDetails={dailyDetails}
            setDailyDetails={setDailyDetails}
            onCancel={handleCancelForm}
            onSuccess={handleSuccessForm}
            showSuccess={showSuccess}
            showError={showError}
            showWarning={showWarning}
          />
        )}
      </main>
    </div>
  );
}

const Header = memo(({ currentPage, setCurrentPage, resetForm }) => {
  const handleListClick = useCallback(() => {
    resetForm();
    setCurrentPage('list');
  }, [resetForm, setCurrentPage]);

  return (
    <header className="events-header">
      <div className="events-header-container">
        <h1 className="events-header-title">Admin Dashboard</h1>
        <nav className="events-header-nav">
          <button
            onClick={handleListClick}
            className={`events-nav-button ${currentPage === 'list' ? 'active' : ''}`}
          >
            <Settings size={18} />
            Gestão de Eventos
          </button>
          <Link to="/admin/custos" className="events-nav-button">
            <Settings size={18} />
            Custos
          </Link>
        </nav>
      </div>
    </header>
  );
});

Header.displayName = 'Header';

const EventsList = memo(({ events, eventImages, loading, onEdit, onDelete, onToggleStatus, onCreate }) => {
  const [filterTab, setFilterTab] = useState('all');

  // ⭐ HOOKS PRIMEIRO - antes de qualquer condicional
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // Separar eventos por status
  const categorizedEvents = useMemo(() => {
    const active = [];
    const inactive = [];
    const past = [];

    events.forEach(event => {
      const eventEndDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
      eventEndDate.setHours(23, 59, 59, 999);
      const isPastEvent = eventEndDate < today;

      if (isPastEvent) {
        past.push(event);
      } else if (event.isActive) {
        active.push(event);
      } else {
        inactive.push(event);
      }
    });

    return { active, inactive, past };
  }, [events, today]);

  // Filtrar eventos baseado no tab
  const filteredEvents = useMemo(() => {
    switch (filterTab) {
      case 'active':
        return categorizedEvents.active;
      case 'inactive':
        return categorizedEvents.inactive;
      case 'past':
        return categorizedEvents.past;
      default:
        return [...categorizedEvents.active, ...categorizedEvents.inactive, ...categorizedEvents.past];
    }
  }, [filterTab, categorizedEvents]);

  // ⭐ AGORA SIM - condicionais DEPOIS dos hooks
  if (loading) {
    return (
      <div className="events-list-container">
        <div className="events-loading">A carregar eventos...</div>
      </div>
    );
  }

  return (
    <div className="events-list-container">
      {/* Header */}
      <div className="events-list-header">
        <div>
          <h2 className="events-list-title">Gestão de Eventos</h2>
          <p className="events-list-subtitle">{events.length} evento(s) total</p>
        </div>
        <button onClick={onCreate} className="events-create-button">
          <Plus size={18} />
          Criar Novo Evento
        </button>
      </div>

      {events.length === 0 ? (
        <div className="events-empty-state">
          <h3>Nenhum evento criado</h3>
          <p>Começa por criar o teu primeiro evento!</p>
        </div>
      ) : (
        <>
          {/* Tabs de filtro */}
          <div className="events-filter-tabs">
            <button
              className={`filter-tab ${filterTab === 'all' ? 'active' : ''}`}
              onClick={() => setFilterTab('all')}
            >
              <span>Todos</span>
              <span className="tab-count">{events.length}</span>
            </button>
            <button
              className={`filter-tab ${filterTab === 'active' ? 'active' : ''}`}
              onClick={() => setFilterTab('active')}
            >
              <CheckCircle size={16} />
              <span>Ativos</span>
              <span className="tab-count">{categorizedEvents.active.length}</span>
            </button>
            <button
              className={`filter-tab ${filterTab === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilterTab('inactive')}
            >
              <EyeOff size={16} />
              <span>Inativos</span>
              <span className="tab-count">{categorizedEvents.inactive.length}</span>
            </button>
            <button
              className={`filter-tab ${filterTab === 'past' ? 'active' : ''}`}
              onClick={() => setFilterTab('past')}
            >
              <AlertCircle size={16} />
              <span>Passados</span>
              <span className="tab-count">{categorizedEvents.past.length}</span>
            </button>
          </div>

          {/* Grid de eventos */}
          {filteredEvents.length === 0 ? (
            <div className="events-empty-filtered">
              <p>Nenhum evento nesta categoria</p>
            </div>
          ) : (
            <div className="events-grid">
              {filteredEvents.map(event => {
                const eventEndDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
                eventEndDate.setHours(23, 59, 59, 999);
                const isPast = eventEndDate < today;

                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    eventImages={eventImages[event.id]}
                    isPast={isPast}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onToggleStatus={onToggleStatus}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
});

EventsList.displayName = 'EventsList';

// ========================================
// EVENTCARD MELHORADO - Com Indicadores Visuais
// ========================================
// Ficheiro: EventsManagementPage.jsx
// Substituir o componente EventCard (linha ~336-427)
// ========================================

const EventCard = memo(({ event, eventImages, isPast, onEdit, onDelete, onToggleStatus }) => {
  const cardData = useMemo(() => {
    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const getDuration = () => {
      if (!event.endDate) return 'Dia único';

      // Usar selectedDates se existir
      if (event.selectedDates && event.selectedDates.length > 0) {
        const days = event.selectedDates.length;
        return `${days} ${days === 1 ? 'dia' : 'dias'}`;
      }

      // Fallback
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return `${days} dias`;
    };

    // Obter a primeira imagem disponível ou placeholder
    let imageUrl = PLACEHOLDER_IMAGE;
    if (eventImages && eventImages.images && eventImages.images.length > 0) {
      imageUrl = eventImages.images[0];
    }

    return {
      formattedStartDate: formatDate(event.startDate),
      duration: getDuration(),
      priceFormatted: event.price.toFixed(2),
      imageUrl
    };
  }, [event.startDate, event.endDate, event.price, event.selectedDates, eventImages]);

  const handleEdit = useCallback(() => onEdit(event), [onEdit, event]);
  const handleDelete = useCallback(() => onDelete(event.id), [onDelete, event.id]);
  const handleToggle = useCallback(() => onToggleStatus(event.id, event.isActive), [onToggleStatus, event.id, event.isActive]);

  // ⭐ Classes CSS baseadas no estado
  const cardClasses = [
    'event-list-card',
    !event.isActive && 'inactive',
    isPast && 'past-event'
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      <div className="event-list-image">
        <img
          src={cardData.imageUrl}
          alt={event.title}
          loading="lazy"
          decoding="async"
        />
        <div className="event-list-badges">
          <span className={`event-type-badge badge-${event.eventType}`}>
            {event.eventType === 'single' ? 'Dia Único' : 'Múltiplos Dias'}
          </span>
          {/* ⭐ Badge de evento passado */}
          {isPast && (
            <span className="event-status-badge past">
              <AlertCircle size={14} />
              Terminado
            </span>
          )}
          {/* ⭐ Badge de inativo */}
          {!isPast && !event.isActive && (
            <span className="event-status-badge inactive">
              <EyeOff size={14} />
              Inativo
            </span>
          )}
        </div>
      </div>

      <div className="event-list-content">
        <h3 className="event-list-title">{event.title}</h3>
        <p className="event-list-description">{event.description}</p>

        <div className="event-list-meta">
          <div className="meta-item">
            <Calendar size={16} />
            <span>{cardData.formattedStartDate}</span>
            {event.endDate && <span className="meta-duration">({cardData.duration})</span>}
          </div>
          <div className="meta-item">
            <span>€{cardData.priceFormatted}</span>
          </div>
          <div className="meta-item capacity">
            <span>{event.currentReservations}/{event.maxReservations} reservas</span>
          </div>
        </div>

        <div className="event-list-actions">
          {/* ⭐ Desabilitar toggle para eventos passados */}
          {!isPast && (
            <button
              onClick={handleToggle}
              className={`action-btn ${event.isActive ? 'deactivate-btn' : 'activate-btn'}`}
              title={event.isActive ? 'Desativar evento' : 'Ativar evento'}
            >
              {event.isActive ? <EyeOff size={18} /> : <Eye size={18} />}
              {event.isActive ? 'Desativar' : 'Ativar'}
            </button>
          )}
          <button onClick={handleEdit} className="action-btn edit-btn">
            <Edit size={18} />
            Editar
          </button>
          <button onClick={handleDelete} className="action-btn delete-btn">
            <Trash2 size={18} />
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
});

EventCard.displayName = 'EventCard';

const EventForm = memo(({
  isEdit,
  editingEvent,
  formData,
  setFormData,
  previewUrls,
  setPreviewUrls,
  selectedDates,
  setSelectedDates,
  dailyDetails,
  setDailyDetails,
  onCancel,
  onSuccess,
  showSuccess,
  showError,
  showWarning
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, [setFormData]);

  const handleEventTypeChange = useCallback((type) => {
    setFormData(prev => ({ ...prev, eventType: type }));
    if (type === 'single') {
      setSelectedDates([]);
      setDailyDetails({});
    }
  }, [setFormData, setSelectedDates, setDailyDetails]);

  const handleImageUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);

    const validFiles = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        showWarning(`Imagem muito grande: ${file.name}`);
        continue;
      }
      validFiles.push(file);
    }

    const imagePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    const images = await Promise.all(imagePromises);
    setPreviewUrls(prev => [...prev, ...images]);
  }, [showWarning, setPreviewUrls]);

  const removeImage = useCallback((index) => {
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [setPreviewUrls]);

  const handleSubmit = useCallback(async () => {
    if (!formData.title.trim()) {
      showWarning('O título é obrigatório');
      return;
    }

    if (formData.eventType === 'multi' && selectedDates.length === 0) {
      showWarning('Selecione pelo menos um dia');
      return;
    }

    if (formData.eventType === 'single' && !formData.startDate) {
      showWarning('A data é obrigatória');
      return;
    }

    if (previewUrls.length === 0) {
      showWarning('Adicione pelo menos uma imagem');
      return;
    }

    setIsSubmitting(true);

    try {
      const isMultiDay = formData.eventType === 'multi';

      const toLocalDateStr = (date) => {
        if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return date;
      };

      const eventData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        startDate: isMultiDay && selectedDates.length > 0
          ? toLocalDateStr(selectedDates[0]) + 'T00:00:00'
          : formData.startDate + 'T00:00:00',
        endDate: isMultiDay && selectedDates.length > 0
          ? toLocalDateStr(selectedDates[selectedDates.length - 1]) + 'T00:00:00'
          : null,
        eventType: formData.eventType,
        price: parseFloat(formData.price),
        maxReservations: parseInt(formData.maxReservations),
        images: previewUrls,
        dailyDetails: isMultiDay && selectedDates.length > 0
          ? selectedDates.map(date => {
            const dateStr = toLocalDateStr(date);
            return {
              date: dateStr,
              image: dailyDetails[dateStr]?.image || '',
              description: dailyDetails[dateStr]?.description || ''
            };
          })
          : [],
        timeSlots: formData.timeSlots.map(slot => ({
          name: slot.name,
          startTime: slot.startTime,
          endTime: slot.endTime,
          price: parseFloat(slot.price),
          maxCapacity: parseInt(slot.maxCapacity)
        }))
      };

      console.log('📤 Enviando evento:', eventData);
      console.log('📅 Datas selecionadas:', selectedDates.map(d => d.toLocaleDateString('pt-PT')));

      if (isEdit && editingEvent) {
        await eventService.updateEvent(editingEvent.id, eventData);
        showSuccess('Evento atualizado!');
      } else {
        await eventService.createEvent(eventData);
        showSuccess('Evento criado!');
      }

      onSuccess();
    } catch (error) {
      console.error('❌ Erro:', error);
      showError(error.message || 'Erro ao processar evento');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedDates, previewUrls, dailyDetails, isEdit, editingEvent, showWarning, showSuccess, showError, onSuccess]);

  const handleSingleTypeClick = useCallback(() => handleEventTypeChange('single'), [handleEventTypeChange]);
  const handleMultiTypeClick = useCallback(() => handleEventTypeChange('multi'), [handleEventTypeChange]);

  return (
    <div className="event-form-container">
      <div className="event-form-header">
        <h2>{isEdit ? 'Editar Evento' : 'Criar Novo Evento'}</h2>
      </div>

      <div className="event-form-content">
        <div className="event-form">
          <div className="form-group">
            <label className="form-label">Tipo de Evento *</label>
            <div className="event-type-grid">
              <button
                type="button"
                onClick={handleSingleTypeClick}
                className={`event-type-card ${formData.eventType === 'single' ? 'active' : ''}`}
                disabled={isSubmitting}
              >
                <Calendar size={24} />
                <div>
                  <div className="event-type-label">Dia Único</div>
                  <div className="event-type-description">Evento num único dia</div>
                </div>
              </button>

              <button
                type="button"
                onClick={handleMultiTypeClick}
                className={`event-type-card ${formData.eventType === 'multi' ? 'active' : ''}`}
                disabled={isSubmitting}
              >
                <Calendar size={24} />
                <div>
                  <div className="event-type-label">Múltiplos Dias</div>
                  <div className="event-type-description">Evento ao longo de vários dias</div>
                </div>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Título *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Nome do evento"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Descrição *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Descreve o evento..."
              rows={5}
              disabled={isSubmitting}
            />
          </div>

          {formData.eventType === 'multi' ? (
            <div className="form-group">
              <label className="form-label">Selecione os Dias *</label>
              <MultiDayCalendar
                selectedDates={selectedDates}
                onDatesChange={setSelectedDates}
                dailyDetails={dailyDetails}
                onDailyDetailsChange={setDailyDetails}
                initialMonth={selectedDates.length > 0 ? selectedDates[0] : null}
              />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Data *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className="form-input"
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Preço (€) *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Vagas Máximas *</label>
              <input
                type="number"
                name="maxReservations"
                value={formData.maxReservations}
                onChange={handleInputChange}
                className="form-input"
                min="1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Imagens *</label>
            <div className="image-upload-section">
              <label className="image-upload-label">
                <span>Clica para adicionar imagens</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="image-upload-input"
                  disabled={isSubmitting}
                />
              </label>

              {previewUrls.length > 0 && (
                <div className="image-preview-grid">
                  {previewUrls.map((url, index) => (
                    <ImagePreview
                      key={index}
                      url={url}
                      index={index}
                      onRemove={removeImage}
                      disabled={isSubmitting}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <TimeSlotManager
            slots={formData.timeSlots}
            onChange={(slots) => setFormData(prev => ({ ...prev, timeSlots: slots }))}
            disabled={isSubmitting}
          />
        </div>

        <div className="form-actions">
          <button onClick={onCancel} className="btn-cancel" disabled={isSubmitting}>
            Cancelar
          </button>
          <button onClick={handleSubmit} className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'A processar...' : isEdit ? 'Atualizar' : 'Criar Evento'}
          </button>
        </div>
      </div>
    </div>
  );
});

EventForm.displayName = 'EventForm';

const ImagePreview = memo(({ url, index, onRemove, disabled }) => {
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [onRemove, index]);

  return (
    <div className="image-preview-item">
      <img src={url} alt={`Preview ${index + 1}`} loading="lazy" decoding="async" />
      <button
        type="button"
        onClick={handleRemove}
        className="image-remove-btn"
        disabled={disabled}
      >
        ×
      </button>
    </div>
  );
});

ImagePreview.displayName = 'ImagePreview';